import { BlackFridayBanner } from 'components/black-friday-banner'
import { Navbar } from 'layout/navbar'
import { Sidebar } from 'layout/sidebar'
import {
	ReactNode,
	useCallback,
	useRef,
	useState,
	type CSSProperties,
	type UIEvent
} from 'react'
import { useUiStore, useAccountStore, useUserStore } from 'store'
import { useLocation } from 'react-router-dom'
import { CreditCardExpiryBanner } from 'components/credit-card-expiry-banner'
import { SurveyHost } from 'components/survey-host'
import { DisconnectedAccountBanner } from 'components/notice/disconnected-account-banner'

export const MainLayout = (props: { children: ReactNode }) => {
	const { children } = props
	const location = useLocation()
	const isSettingsPage = location.pathname.includes('/settings')
	// The embedded CMS owns its scrollbar; avoid reserving a second gutter.
	const isContentAdminPage = /^\/admin\/docs(?:\/|$)/.test(location.pathname)
	const desktopSidebarCollapsed = useUiStore(
		(state) => state.desktopSidebarCollapsed
	)
	// Which rail is rendering decides the offset: the DS rail is exactly
	// 240/74 and morphs on its own 340ms clock, the admin rail is still
	// w-auto and keeps the numbers it always had.
	const isAdmin = useUiStore((state) => state.context) === 'admin'
	const { account } = useAccountStore()
	const userId = useUserStore((state) => state.user?.id)
	const campaignKey = `${account?.id || ''}:${userId || ''}`
	const campaignKeyRef = useRef(campaignKey)
	campaignKeyRef.current = campaignKey
	const [bannerLayout, setBannerLayout] = useState({
		key: campaignKey,
		height: 0
	})
	const onBannerHeightChange = useCallback(
		(height: number) => {
			if (campaignKeyRef.current === campaignKey)
				setBannerLayout({ key: campaignKey, height })
		},
		[campaignKey]
	)
	const bannerHeight =
		bannerLayout.key === campaignKey ? bannerLayout.height : 0
	const showCampaign =
		!isAdmin && !!account && account.id !== 'workspace-demo' && !!userId

	// Fade scrollbar (ported from the PA report): the canvas thumb is
	// transparent at rest and fades in only while actively scrolling, cleared
	// ~700ms after the last scroll. CSS owns the fade (.app-page-canvas /
	// .is-scrolling in App.css); this just toggles the class.
	const scrollIdle = useRef<ReturnType<typeof setTimeout>>()
	const handleCanvasScroll = (e: UIEvent<HTMLElement>) => {
		const el = e.currentTarget
		el.classList.add('is-scrolling')
		clearTimeout(scrollIdle.current)
		scrollIdle.current = setTimeout(
			() => el.classList.remove('is-scrolling'),
			700
		)
	}

	const paymentMethodStatus = account?.billing?.payment_method?.status
	const subscriptionStatus = account?.billing?.subscription?.status
	const shouldShowCcExpiryBanner =
		paymentMethodStatus === 'EXPIRING_SOON' && subscriptionStatus === 'ACTIVE'

	return (
		<div
			className="flex h-[100dvh] flex-col"
			style={
				{
					'--cg-announcement-height': `${showCampaign ? bannerHeight : 0}px`
				} as CSSProperties
			}
		>
			{showCampaign && (
				<BlackFridayBanner
					key={`${account!.id}:${userId}`}
					accountId={account!.id}
					userId={userId!}
					onHeightChange={onBannerHeightChange}
				/>
			)}
			<div className="flex flex-1 min-h-0">
				<Sidebar />
				<div
					className={`
						app-shell-frame relative flex-1 flex flex-col h-full min-h-0 overflow-hidden bg-table-band p-[10px]
						${
							isAdmin
								? 'transition-all duration-300'
								: 'transition-[margin] duration-[340ms] ease-[cubic-bezier(0.16,1,0.3,1)]'
						}
						${
							isAdmin
								? desktopSidebarCollapsed
									? 'md:ml-20'
									: 'md:ml-[240px]'
								: desktopSidebarCollapsed
								? 'md:ml-[var(--cg-sb-w-shut)]'
								: 'md:ml-[var(--cg-sb-w)]'
						}
					`}
				>
					<Navbar />
					<main
						data-virtuoso-scroll-parent="true"
						onScroll={handleCanvasScroll}
						style={isContentAdminPage ? { scrollbarGutter: 'auto' } : undefined}
						className="app-page-canvas flex-1 min-h-0 overflow-y-auto [scrollbar-gutter:stable] overflow-x-clip overscroll-none rounded-b-[12px] border-x border-b border-t-traffic bg-[#fafafa]
						bg-[linear-gradient(0deg,rgba(238,236,255,0.06),rgba(238,236,255,0.06)),#ffffff]
						shadow-[0_12px_32px_-4px_rgba(16,24,40,0.04),0_4px_12px_-2px_rgba(16,24,40,0.03)]"
					>
						{shouldShowCcExpiryBanner && !isSettingsPage && (
							<CreditCardExpiryBanner />
						)}
						{!isAdmin && !isSettingsPage && <DisconnectedAccountBanner />}
						{children}
					</main>
					<SurveyHost />
				</div>
			</div>
		</div>
	)
}
